<template>
    <div v-bind:id="id" class="dropzone dz-clickable" v-on:drop="handleDrop" v-on:dragover="handleDragOver">
        <div class="dz-message" v-on:click="handleClick">
            <slot v-bind:max-filesize="maxFilesize" v-bind:accepted-file-types="acceptedFileTypes">
                <div class="row row-middle row-tall">
                    <div class="column text-center">
                        <div class="card-figure m-b-2">
                            <span class="i-container i-tone-accent">
                                <svg xmlns="http://www.w3.org/2000/svg" class="icon i-size-xlarge" viewBox="0 0 640 512"><path d="M476 106.7l96 144c4.9 7.4 5.4 16.8 1.2 24.6S560.9 288 552 288H408 360 280c-9.2 0-17.6-5.3-21.6-13.6s-2.9-18.2 2.9-25.4l64-80c4.6-5.7 11.4-9 18.7-9s14.2 3.3 18.7 9l17.3 21.6 56-84C440.5 100 448 96 456 96s15.5 4 20 10.7zM64 128H192v64c-17.7 0-32 14.3-32 32V416c0 17.7 14.3 32 32 32H320c17.7 0 32-14.3 32-32V352H512v96c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V192c0-35.3 28.7-64 64-64zm8 64c-8.8 0-16 7.2-16 16v16c0 8.8 7.2 16 16 16H88c8.8 0 16-7.2 16-16V208c0-8.8-7.2-16-16-16H72zm0 104c-8.8 0-16 7.2-16 16v16c0 8.8 7.2 16 16 16H88c8.8 0 16-7.2 16-16V312c0-8.8-7.2-16-16-16H72zm0 104c-8.8 0-16 7.2-16 16v16c0 8.8 7.2 16 16 16H88c8.8 0 16-7.2 16-16V416c0-8.8-7.2-16-16-16H72zm336 16v16c0 8.8 7.2 16 16 16h16c8.8 0 16-7.2 16-16V416c0-8.8-7.2-16-16-16H424c-8.8 0-16 7.2-16 16z"></path><path fill-opacity="0.4" d="M192 64c0-35.3 28.7-64 64-64H576c35.3 0 64 28.7 64 64V288c0 35.3-28.7 64-64 64H256c-35.3 0-64-28.7-64-64V64zM456 96c-8 0-15.5 4-20 10.7l-56 84L362.7 169c-4.6-5.7-11.5-9-18.7-9s-14.2 3.3-18.7 9l-64 80c-5.8 7.2-6.9 17.1-2.9 25.4s12.4 13.6 21.6 13.6h80 48H552c8.9 0 17-4.9 21.2-12.7s3.7-17.3-1.2-24.6l-96-144C471.5 100 464 96 456 96zM304 128a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"></path></svg>
                            </span>
                        </div>
                        <div class="card-content">
                            <h2 class="card-title f-s-5 f-w-bold m-b-1 f-w-regular">{{ trans('Drag & Drop') }}</h2>
                            <p class="f-s-4 c-muted">{{ trans('or browse your computer for a file (max upload limit :max MB per file)', {max: maxFilesize}) }}</p>
                        </div>
                    </div>
                </div>
            </slot>
        </div>
        <input
            type="file"
            ref="input"
            style="display: none;"
            v-bind:accept="acceptedFileTypes"
            v-bind:multiple="maxFiles !== 1"
            v-on:change="handleInputChange"
        >
    </div>
</template>
<script>
import PresignedUploader from '../../upload/PresignedUploader.js';

export default {
    props: {
        id: {
            type: String
        },
        acceptedFileTypes: {
            type: String
        },
        maxFiles: {
            type: Number,
            default: 1
        },
        maxFilesize: {
            type: Number,
            default: 50
        },
        // "context" is passed straight through to presign/commit — it's
        // how the backend decides visibility and Content-Disposition
        // (e.g. "download" for private, attachment-served files).
        context: {
            type: String,
            default: null
        },
        // Audio deliberately skips the commit step (server-side processing
        // does the equivalent work on save) — see _docs/audio-lessons-spec.md §3.
        skipCommit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            activeUploads: {}
        }
    },
    methods: {
        handleDrop(event) {
            event.preventDefault();
            event.stopPropagation();

            this.processFiles(event.dataTransfer ? event.dataTransfer.files : []);
        },
        handleDragOver(event) {
            event.preventDefault();
            event.stopPropagation();

            event.dataTransfer.dropEffect = 'copy';
        },
        handleClick() {
            this.$refs.input.click();
        },
        handleInputChange(event) {
            this.processFiles(event.target.files);

            // Allow selecting the exact same file again later (e.g. retry).
            event.target.value = null;
        },
        processFiles(fileList) {
            const files = Array.from(fileList || []);

            // this.maxFiles is null when unlimited (the FileUpload.vue
            // default) — Array.slice(0, null) treats null as 0 and would
            // silently drop every file, so it's only applied as a cap when
            // it's actually a positive number.
            const limited = this.maxFiles > 0 ? files.slice(0, this.maxFiles) : files;

            limited.forEach((file) => this.startUpload(file));
        },
        startUpload(file) {
            const uploadId = this.generateUploadId();

            this.$emit('upload:added', {uploadId, file});

            if ( ! this.isAcceptedFileType(file)) {
                this.$emit('upload:error', {uploadId, file, message: this.trans('This file type is not accepted.')});
                return;
            }

            if ( ! this.isWithinSizeLimit(file)) {
                this.$emit('upload:error', {uploadId, file, message: this.trans('File size too large. Max file size is :max MB.', {max: this.maxFilesize})});
                return;
            }

            const uploader = new PresignedUploader({
                presignUrl: window.Kourses.activeBaseUrl + '/upload/presign',
                commitUrl: this.skipCommit ? null : window.Kourses.activeBaseUrl + '/upload/commit',
                abandonUrl: window.Kourses.activeBaseUrl + '/upload/abandon',
                context: this.context,
            });

            this.activeUploads[uploadId] = uploader;

            uploader.upload(file, {
                onProgress: (sent, total) => {
                    this.$emit('upload:progress', {uploadId, file, sent, total});
                },
                onSuccess: (committed) => {
                    delete this.activeUploads[uploadId];
                    this.$emit('upload:success', {uploadId, file, committed});
                },
                onError: (message) => {
                    delete this.activeUploads[uploadId];
                    this.$emit('upload:error', {uploadId, file, message});
                },
            });
        },
        /**
         * Cancel an in-progress upload (e.g. the user removed the row
         * before it finished) — aborts the request and cleans up the
         * temporary S3 object rather than leaving it to the lifecycle rule.
         */
        cancelUpload(uploadId) {
            if (this.activeUploads[uploadId]) {
                this.activeUploads[uploadId].cancel();
                delete this.activeUploads[uploadId];
            }
        },
        reset() {
            if (this.$refs.input) {
                this.$refs.input.value = null;
            }
        },
        isAcceptedFileType(file) {
            if ( ! this.acceptedFileTypes) {
                return true;
            }

            const fileName = file.name.toLowerCase();
            const mimeType = (file.type || '').toLowerCase();

            return this.acceptedFileTypes
                .split(',')
                .map((pattern) => pattern.trim().toLowerCase())
                .some((pattern) => {
                    if (pattern.startsWith('.')) {
                        return fileName.endsWith(pattern);
                    }

                    if (pattern.endsWith('/*')) {
                        return mimeType.startsWith(pattern.slice(0, -1));
                    }

                    return mimeType === pattern;
                });
        },
        isWithinSizeLimit(file) {
            return file.size <= this.maxFilesize * 1024 * 1024;
        },
        generateUploadId() {
            return 'upload-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        },
    },
}
</script>
